import { useCallback, useEffect, useState } from "react";
import { Layers, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, PageHeader, Pager, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CTR_STATUS, ContractView, NewContractDialog } from "./ContractDialogs";
import { BulkContractDialog } from "./BulkContractDialog";

export const ContractsPanel = ({ employeeId }) => {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 20 });
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [view, setView] = useState(null);
  const load = useCallback(() => api.get("/rrhh/contracts", { params: { q, status, page, employee_id: employeeId || "" } }).then((r) => setData(r.data)), [q, status, page, employeeId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="syc-card overflow-hidden" data-testid="contracts-panel">
      <div className="flex flex-col lg:flex-row gap-3 p-4 border-b border-[#e4e8e1]">
        {!employeeId && <div className="relative flex-1"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a9584]" />
          <Input data-testid="contracts-search-input" className="pl-9" placeholder="Buscar por código, nombre o RUT" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} /></div>}
        <div className="flex flex-wrap gap-2 flex-1">{[["", "Todos"], ...Object.entries(CTR_STATUS).map(([k, v]) => [k, v[1]])].map(([k, l]) => (
          <Button key={k || "all"} size="sm" data-testid={`contracts-filter-${k || "all"}`} variant={status === k ? "default" : "outline"} className="rounded-full" onClick={() => { setStatus(k); setPage(1); }}>{l}</Button>))}</div>
        {can("rrhh.contratos.crear") && !employeeId && <Button data-testid="bulk-contract-btn" variant="outline" className="rounded-full" onClick={() => setBulk(true)}><Layers size={16} className="mr-1" />Generación masiva</Button>}
        {can("rrhh.contratos.crear") && <Button data-testid="create-contract-btn" className="rounded-full" onClick={() => setOpen(true)}><Plus size={16} className="mr-1" />Generar</Button>}
      </div>
      {data.items.length === 0 ? <EmptyState title="Sin contratos" /> : data.items.map((c) => (
        <button key={c.id} data-testid={`contract-row-${c.id}`} onClick={() => setView(c.id)} className="w-full flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] hover:bg-[#f6fbf2] text-sm text-left">
          <span className="font-mono text-xs w-28">{c.code}</span>
          <div className="flex-1 min-w-[180px]"><div className="font-semibold">{c.employee_name}</div><div className="text-xs text-[#5b6656]">{c.kind === "anexo" ? `Anexo de ${c.parent_code}` : "Contrato"} · {c.template_name}</div></div>
          <span className="text-xs text-[#5b6656]">{fmtDate(c.start_date)} → {c.end_date ? fmtDate(c.end_date) : "indefinido"}</span>
          <StatusBadge status={CTR_STATUS[c.status][0]} label={CTR_STATUS[c.status][1]} />
        </button>
      ))}
      <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} />
      <NewContractDialog open={open} onOpenChange={setOpen} employeeId={employeeId} onSaved={(c) => { setOpen(false); load(); setView(c.id); }} />
      <BulkContractDialog open={bulk} onOpenChange={setBulk} onDone={load} />
      <ContractView id={view} onClose={() => setView(null)} onChanged={load} />
    </div>
  );
};

export default function Contracts() {
  return (
    <div data-testid="contracts-page">
      <PageHeader eyebrow="RR.HH." title="Contratos y anexos" subtitle="Se generan desde plantillas: borrador → vigente → terminado, con PDF, archivo firmado y trazabilidad." />
      <ContractsPanel />
    </div>
  );
}
